import {
  faH,
  faH1,
  faH2,
  faH3,
  faH4,
  faH5,
  faH6,
  faItalic,
  faLink,
  faListOl,
  faListUl,
} from "@fortawesome/pro-regular-svg-icons";
import { faBold } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link as ExtensionLink } from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { Editor, EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { Upload as TusUpload } from "tus-js-client";
import Button from "../components/Button";
import FileUpload from "../components/FileUpload";
import MediaBox from "../components/MediaBox";
import ToggleSwitch from "../components/ToggleSwitch";
import { Feedback, FileType } from "../helpers/types";
import { useApplicationContext } from "../providers/ApplicationProvider";
import { useAuthContext } from "../providers/AuthProvider";
import { useConfigContext } from "../providers/ConfigProvider";
import { useToast } from "../components/ToastProvider";

export const MediaContainer = () => <Outlet />;

const ApplicationMedia = () => {
  const { t } = useTranslation();
  const { apiCall } = useAuthContext();
  const { application } = useApplicationContext();
  if (!application) return false;

  

  const navigate = useNavigate();

  const images = application.media?.filer_bilder_multiple;
  const videos = application.media?.filer_video_multiple;
  const tus_videos = application.media?.tus;

  const [content, setContent] = useState<string>(
    application.confirmation_content?.share_history_text ?? "",
  );
  const [isSaving, setSaving] = useState<boolean>(false);
  const [feedback, setFeedback] = useState<Feedback | null>(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      ExtensionLink.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: "https",
      }),
      Placeholder.configure({
        placeholder: "Fyll inn her",
      }),
    ],
    content,
    editorProps: {
      attributes: {
        class: "form-control rounded-top-0 border-top-0",
        style: "min-height: 140px;",
      },
    },
    onUpdate: ({ editor }) => {
      handleChange(editor.getHTML());
    },
  });

  const handleChange = (newContent: string) => {
    handleContentChange(newContent);
  };

  const handleContentChange = (reason: any) => {
    setContent(reason);
  };

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);

    try {
      const response = await apiCall({
        action: "saveShareHistoryText",
        application: application?.id,
        content,
      });

      if (response?.data?.success) {
        setFeedback({
          success: true,
          message: t("changes_saved_successfully"),
        });
      } else {
        setFeedback({
          success: false,
          message: t("changes_not_saved"),
        });
      }
    } catch (error) {
      console.error("SendMessage ERROR: ", error);
      setFeedback({
        success: false,
        message: t("changes_not_saved"),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="row gx-5">
        <div className="col-lg-6">
          <h4 className="mb-3">{t("media_title")}</h4>
          <p>{t("media_description")}</p>

          <div className="my-2">
            <h6>{t("media_images")}</h6>
            <div className="d-flex gap-2 flex-wrap">
              {images &&
                images.length &&
                images.map((image, index) => (
                  <MediaBox key={index} {...image} />
                ))}
            </div>
            <Button
              title={t("media_image_upload")}
              type="linkbtn"
              classes="p-0 mt-2 text-primary"
              onClick={() => navigate("bilder")}
            />
          </div>

          <div className="mt-4">
            <h6>{t("media_videos")}</h6>
            <div className="d-flex gap-2 flex-wrap">
              {tus_videos &&
                tus_videos.length > 0 &&
                tus_videos.map((video, index) => (
                  <MediaBox key={index} {...video} />
                ))}
              {videos &&
                videos.length > 0 &&
                videos.map((video, index) => (
                  <MediaBox key={index} {...video} />
                ))}
            </div>
            <Button
              title={t("media_video_upload")}
              type="linkbtn"
              classes="p-0 mt-2 text-primary"
              onClick={() => navigate("video")}
            />
          </div>
        </div>

        <div className="col-lg-6">
          <h4 className="mb-3">{t("share_history_title")}</h4>
          <p>{t("share_history_description")}</p>

          <div className="mt-4">
            <Toolbar editor={editor} />
            <EditorContent editor={editor} />
            <div className="d-flex justify-content-between mt-4">
              <span
                className={feedback?.success ? "text-success" : "text-danger"}
              >
                {feedback ? feedback.message : null}
              </span>
              <Button
                title="Lagre"
                type="primary"
                onClick={handleSave}
                loading={isSaving}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export const UploadMedia = () => {
  const { apiCall } = useAuthContext();
  const { application, loadApplication } = useApplicationContext();
  const { showToast } = useToast();
  const [files, setFiles] = useState<(FileType & { preview: string })[]>([]);
  const [isUploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [consent, setConsent] = useState(false);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const { data } = useConfigContext();

  const uploadMedia = async (key: string) => {
    if (!key) return false;

    if (key === "bilder") {
      return uploadImages();
    } else {
      return uploadVideos();
    }
  };

  const uploadVideos = async () => {
    const file = files[0];
    if (!file) return;

    
    try {
      setUploading(true);

      // Step 1: Get TUS upload URL from backend
      const uploadUrlResponse = await apiCall({
        action: "generateCloudflareTUSUrl",
        upload_length: file.size,
      });


      const tus_url: string | undefined = uploadUrlResponse?.data?.url;

      if (!tus_url) {
        showToast(uploadUrlResponse?.data?.message ?? t('error_generic_api'), 'danger');
        return;
      }

      // Step 2: Upload via TUS to Cloudflare
      let videoId: string | undefined;
      await new Promise<void>((resolve, reject) => {
        const upload = new TusUpload(file, {
          uploadUrl: tus_url,
          chunkSize: 50 * 1024 * 1024,
          retryDelays: [0, 3000, 5000, 10000, 20000],
          metadata: {
            filename: file.name,
            filetype: file.type,
          },
          onError: reject,
          onProgress: (bytesUploaded, bytesTotal) => {
            setUploadProgress(Math.round((bytesUploaded / bytesTotal) * 100));
          },
          onSuccess: () => {
            const url = upload.url;
            if (url) {
              videoId = new URL(url).pathname.split("/").pop();
            }
            resolve();
          },
        });
        upload.start();
      });

      if (!videoId) {
        showToast(t('error_video_id'), 'danger');
        return;
      }

      // Step 3: Save video ID to application
      const response = await apiCall({
        action: "saveCloudflareVideoData",
        videoId,
        application_id: application?.id,
        filename: file.name,
      });

      if (response?.data?.status === 200) {
        await loadApplication();
        navigate(-1);
      } else {
        showToast(response?.data?.message ?? t('error_generic_api'), 'danger');
      }
    } catch (error) {
      console.error("Error uploading video: ", error);
      showToast(t('error_video_upload'), 'danger');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const uploadImages = async () => {
    try {
      setUploading(true);
      const response = await apiCall({
        action: "uploadFile",
        filer_bilder_multiple: files,
        application_id: application?.id,
        type: "media",
      });

      if (response?.data?.status === 200) {
        await loadApplication();
        navigate(-1);
      } else {
        showToast(response?.data?.message ?? t('error_generic_api'), 'danger');
      }
    } catch (error) {
      console.error("Error uploading media: ", error);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (newFiles: (FileType & { preview: string })[]) => {
    if (!newFiles || newFiles.length === 0) return;

    const currentFiles = files;
    const updatedFiles = [
      ...currentFiles.filter(
        (currentFile) =>
          !newFiles.some((newFile) => newFile.name === currentFile.name),
      ),
      ...newFiles,
    ];
    setFiles(updatedFiles);
  };

  const removeFile = (fileIndexToRemove: number) => {
    const currentFiles = files;
    const updatedFiles = currentFiles.filter(
      (_, index) => index !== fileIndexToRemove,
    );
    setFiles(updatedFiles);
  };

  const extensionsFormat =
    params.mediaType === "bilder"
      ? [".jpeg", ".jpg", ".png", ".webp"]
      : [".mp4", ".mov"];

  return (
    <div className="row">
      <h2 className="mb-5">{t(`upload_${params.mediaType}`)}</h2>

      <div className="col-lg-8">
        <FileUpload
          extensionsFormat={extensionsFormat}
          onHandleDrop={handleDrop}
          onRemoveFile={removeFile}
          files={files}
          error=""
          placeholder={t(`upload_${params.mediaType}_placeholder`)}
          name={`upload_${params.mediaType}`}
          maxFileSizeMB={
            params.mediaType === "bilder"
              ? undefined
              : (data?.max_file_size_mb ?? 100)
          }
        />

        <div className="row mt-4">
          <ToggleSwitch
            label={t("upload_media_consent_label")}
            description={t("upload_media_consent_text")}
            value={consent}
            onCheck={() => setConsent(!consent)}
          />
        </div>

        {isUploading && params.mediaType !== "bilder" && (
          <div className="row mt-3">
            <div className="col-12">
              <div className="progress">
                <div
                  className="progress-bar progress-bar-striped progress-bar-animated"
                  role="progressbar"
                  style={{ width: `${uploadProgress}%` }}
                  aria-valuenow={uploadProgress}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  {uploadProgress}%
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="row justify-content-between mt-4">
          <div className="col-auto">
            <Button
              title="Avbryt"
              type="outline-primary"
              leftIcon="xmark"
              onClick={() => navigate(-1)}
            />
          </div>
          <div className="col-auto">
            <Button
              title="Last opp"
              rightIcon="upload"
              onClick={() => uploadMedia(params.mediaType ?? "")}
              disabled={!consent}
              loading={isUploading}
              loadingText={t("upload_loading")}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const Toolbar = ({ editor }: { editor: Editor | null }) => {
  if (!editor) return null;
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const selectHeading = (level: any) => {
    editor.chain().focus().toggleHeading({ level }).run();
    setIsDropdownOpen(false);
  };
  const getActiveHeadingLevel = () => {
    for (let level = 1; level <= 6; level++) {
      if (editor.isActive("heading", { level })) {
        return level;
      }
    }
    return null;
  };
  const renderActiveHeadingIcon = () => {
    const activeLevel = getActiveHeadingLevel();
    switch (activeLevel) {
      case 1:
        return <FontAwesomeIcon icon={faH1} className="button-item" />;
      case 2:
        return <FontAwesomeIcon icon={faH2} className="button-item" />;
      case 3:
        return <FontAwesomeIcon icon={faH3} className="button-item" />;
      case 4:
        return <FontAwesomeIcon icon={faH4} className="button-item" />;
      case 5:
        return <FontAwesomeIcon icon={faH5} className="button-item" />;
      case 6:
        return <FontAwesomeIcon icon={faH6} className="button-item" />;
      default:
        return <FontAwesomeIcon icon={faH} className="button-item" />;
    }
  };

  /* Handle link insert */
  const setLink = useCallback(() => {
    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("URL", previousUrl);

    // cancelled
    if (url === null) {
      return;
    }

    // empty
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();

      return;
    }

    // update link
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  return (
    <div className="bg-light rounded-top-2 form-control border-bottom-0 rounded-bottom-0 d-flex gap-2">
      <div className="dropdown">
        <button
          className={`tiptap-toolbar-button ${editor.isActive("heading") ? "bg-dark text-white" : "text-dark"} dropdown-toggle dropdown-toggle-no-caret`}
          type="button"
          data-bs-toggle="dropdown"
          data-bs-auto-close="inside"
          aria-expanded="false"
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        >
          {renderActiveHeadingIcon()}
        </button>
        {isDropdownOpen && (
          <ul
            className="dropdown-menu show"
            style={{ position: "absolute", top: "100%", left: 0 }}
          >
            {[1, 2, 3, 4, 5, 6].map((level) => (
              <button
                key={level}
                onClick={(e) => {
                  e.preventDefault();
                  selectHeading(level);
                }}
                className={`dropdown-item ${editor.isActive("heading", { level }) ? "text-primary" : "text-black"}`}
              >
                Heading {level}
              </button>
            ))}
          </ul>
        )}
      </div>
      <button
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={`tiptap-toolbar-button ${editor.isActive("bold") ? "bg-dark text-white" : "text-dark"}`}
      >
        <FontAwesomeIcon icon={faBold} className="button-item" />
      </button>
      <button
        onClick={(e) => {
          e.preventDefault();
          editor.chain().focus().toggleItalic().run();
        }}
        className={`tiptap-toolbar-button ${editor.isActive("italic") ? "bg-dark text-white" : "text-dark"}`}
      >
        <FontAwesomeIcon icon={faItalic} className="button-item" />
      </button>

      <button
        onClick={(e) => {
          e.preventDefault();
          editor.chain().focus().toggleBulletList().run();
        }}
        className={`tiptap-toolbar-button ms-3 ${editor.isActive("bulletList") ? "bg-dark text-white" : "text-dark"}`}
      >
        <FontAwesomeIcon icon={faListUl} className="button-item" />
      </button>
      <button
        onClick={(e) => {
          e.preventDefault();
          editor.chain().focus().toggleOrderedList().run();
        }}
        className={`tiptap-toolbar-button ${editor.isActive("orderedList") ? "bg-dark text-white" : "text-dark"}`}
      >
        <FontAwesomeIcon icon={faListOl} className="button-item" />
      </button>
      <button onClick={setLink} className={`tiptap-toolbar-button text-dark`}>
        <FontAwesomeIcon icon={faLink} className="button-item" />
      </button>
    </div>
  );
};

export default ApplicationMedia;
